import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ApiBackend } from "../../providers/apibackend";
import { POSSetting } from "../../model/POSSetting";

/**
 * Fristående, mobiloptimerad incheckningssida (/mobilepos).
 * Skannar QR (order-publicId) med telefonens kamera och checkar in (levererar)
 * produkter enligt vald POS-stations produktlista. Återanvänder Kontrollens
 * inloggning och samma deliver-endpoint som handscanner-appen.
 */

type OrderItem = {
    productId: string;
    productVariantId: string;
    productVariantPriceGroupId: string;
    productVariantOccationId?: string;
    numberOfProducts: number;
    productName?: string;
    variantName?: string;
    occationName?: string;
};

type Delivery = {
    productId: string;
    variantId?: string;
    productVariantId?: string;
    priceGroupId?: string;
    productVariantPriceGroupId?: string;
    occationId?: string;
    productVariantOccationId?: string;
    numDelivered?: number;
};

type Feedback = { type: "success" | "warning" | "error"; title: string; detail?: string } | null;

const READER_ID = "mobilepos-reader";

const numDeliveredFor = (item: OrderItem, deliveries: Delivery[]): number => {
    return deliveries
        .filter(d =>
            d.productId === item.productId &&
            (d.variantId ?? d.productVariantId) === item.productVariantId &&
            (d.priceGroupId ?? d.productVariantPriceGroupId) === item.productVariantPriceGroupId &&
            ((d.occationId ?? d.productVariantOccationId) || "") === (item.productVariantOccationId || ""))
        .reduce((sum, d) => sum + (d.numDelivered ?? 0), 0);
};

const isCheckinTarget = (item: OrderItem, pos: POSSetting): boolean => {
    const list = pos?.ProductList ?? [];
    return list.indexOf(item.productVariantOccationId || "") >= 0
        || list.indexOf(item.productVariantId) >= 0
        || list.indexOf(item.productId) >= 0;
};

const MobilePos = () => {
    const backend = new ApiBackend();
    const [posSettings, setPosSettings] = useState<POSSetting[]>(null);
    const [selectedPos, setSelectedPos] = useState<POSSetting>(null);
    const [scanning, setScanning] = useState(false);
    const [busy, setBusy] = useState(false);
    const [feedback, setFeedback] = useState<Feedback>(null);
    const [order, setOrder] = useState<any>(null);
    const [deliveries, setDeliveries] = useState<Delivery[]>([]);

    const scannerRef = useRef<Html5Qrcode>(null);
    const lastScanRef = useRef<{ code: string; at: number }>({ code: "", at: 0 });
    const handlingRef = useRef(false);

    useEffect(() => {
        backend.listPOSSettings().then(list => {
            setPosSettings((list || []).filter(p => p.showCheckin));
        });
        return () => { stopScanner(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopScanner = () => {
        const s = scannerRef.current;
        if (s) {
            s.stop().then(() => s.clear()).catch(() => { });
            scannerRef.current = null;
        }
    };

    const startScanner = async () => {
        setFeedback(null);
        setScanning(true);
        // Vänta in att reader-diven renderats
        setTimeout(async () => {
            try {
                const scanner = new Html5Qrcode(READER_ID, false);
                scannerRef.current = scanner;
                await scanner.start(
                    { facingMode: "environment" },
                    { fps: 10, qrbox: { width: 240, height: 240 } },
                    (decoded) => onScan(decoded),
                    () => { }
                );
            } catch (e) {
                setScanning(false);
                setFeedback({ type: "error", title: "Kameran kunde inte startas", detail: "Ge sidan kameråtkomst och försök igen." });
            }
        }, 50);
    };

    const onScan = async (decodedText: string) => {
        const now = Date.now();
        // Debounce: ignorera samma kod inom 2,5s och medan ett anrop pågår
        if (handlingRef.current) { return; }
        if (decodedText === lastScanRef.current.code && now - lastScanRef.current.at < 2500) { return; }
        lastScanRef.current = { code: decodedText, at: now };
        handlingRef.current = true;
        try {
            await handleCode(decodedText);
        } finally {
            handlingRef.current = false;
        }
    };

    const loadDeliveries = async (orderId: string): Promise<Delivery[]> => {
        const d = await backend.getOrderDeliveries(orderId);
        setDeliveries(d || []);
        return d || [];
    };

    const handleCode = async (rawCode: string) => {
        setBusy(true);
        setFeedback(null);
        const code = (rawCode || "").trim().replace(/\+/g, "-");
        const fetched = await backend.retrievePublicOrder(code);

        if (!fetched) {
            setOrder(null); setDeliveries([]);
            setFeedback({ type: "error", title: "Okänd order", detail: code });
            beep(false);
            setBusy(false);
            return;
        }
        if (fetched.Status !== "Completed") {
            setOrder(fetched); setDeliveries([]);
            setFeedback({ type: "error", title: "Ogiltig order", detail: "Status: " + fetched.Status });
            beep(false);
            setBusy(false);
            return;
        }

        setOrder(fetched);
        const dels = await loadDeliveries(fetched.id);

        const items: OrderItem[] = (fetched.items || []).filter((it: OrderItem) => isCheckinTarget(it, selectedPos));
        if (items.length === 0) {
            setFeedback({ type: "warning", title: "Ingen produkt för denna station", detail: contactName(fetched) });
            beep(false);
            setBusy(false);
            return;
        }

        // Leverera första matchande rad som inte är fullt incheckad
        const toDeliver = items.find(it => numDeliveredFor(it, dels) < it.numberOfProducts);
        if (!toDeliver) {
            setFeedback({ type: "warning", title: "Redan incheckad", detail: contactName(fetched) });
            beep(false);
            setBusy(false);
            return;
        }

        const ok = await deliverItem(fetched, toDeliver, dels);
        setBusy(false);
        if (!ok) {
            setFeedback({ type: "error", title: "Incheckning misslyckades", detail: labelForItem(toDeliver) });
            beep(false);
        }
    };

    const deliverItem = async (ord: any, item: OrderItem, currentDeliveries: Delivery[]): Promise<boolean> => {
        const ok = await backend.deliverCheckinProduct(
            ord.id, item.productId, item.productVariantId, item.productVariantPriceGroupId,
            item.productVariantOccationId || "", 1, selectedPos?.Id
        );
        if (ok) {
            const dels = await loadDeliveries(ord.id);
            const remaining = (ord.items || [])
                .filter((it: OrderItem) => isCheckinTarget(it, selectedPos))
                .reduce((sum: number, it: OrderItem) => sum + Math.max(0, it.numberOfProducts - numDeliveredFor(it, dels)), 0);
            setFeedback({
                type: "success",
                title: "Incheckad — " + contactName(ord),
                detail: labelForItem(item) + (remaining > 0 ? ` · ${remaining} kvar i ordern` : "")
            });
            beep(true);
        }
        return ok;
    };

    const manualDeliver = async (item: OrderItem) => {
        if (!order || busy) { return; }
        setBusy(true);
        const ok = await deliverItem(order, item, deliveries);
        setBusy(false);
        if (!ok) {
            setFeedback({ type: "error", title: "Incheckning misslyckades", detail: labelForItem(item) });
        }
    };

    const contactName = (ord: any): string => {
        const c = ord && ord.contact;
        return c ? `${c.firstName || ""} ${c.lastName || ""}`.trim() : "";
    };

    const labelForItem = (item: OrderItem): string => {
        return [item.productName, item.variantName, item.occationName].filter(Boolean).join(" · ");
    };

    const beep = (success: boolean) => {
        try {
            const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain); gain.connect(ctx.destination);
            osc.frequency.value = success ? 880 : 220;
            gain.gain.value = 0.15;
            osc.start();
            setTimeout(() => { osc.stop(); ctx.close(); }, success ? 120 : 320);
        } catch { /* ignore */ }
    };

    // ---------- Render ----------
    const bannerColor = (t: string) => t === "success" ? "#1f7a3d" : t === "warning" ? "#b07d1e" : "#b3382a";

    if (posSettings === null) {
        return <div style={styles.center}>Laddar stationer…</div>;
    }

    if (!selectedPos) {
        return (
            <div style={styles.page}>
                <div style={styles.header}>
                    <span>Incheckning</span>
                    <button style={styles.linkBtn} onClick={() => ApiBackend.firebase.auth().signOut()}>Logga ut</button>
                </div>
                <div style={styles.body}>
                    <h2 style={{ marginTop: 0 }}>Välj station</h2>
                    {posSettings.length === 0 &&
                        <p style={{ color: "#888" }}>Inga stationer med incheckning aktiverad hittades.</p>}
                    <div style={styles.stationList}>
                        {posSettings.map(p =>
                            <button key={p.Id} style={styles.stationBtn} onClick={() => setSelectedPos(p)}>
                                {p.Name}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const targetItems: OrderItem[] = order ? (order.items || []).filter((it: OrderItem) => isCheckinTarget(it, selectedPos)) : [];

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <button style={styles.linkBtn} onClick={() => { stopScanner(); setScanning(false); setSelectedPos(null); setOrder(null); setFeedback(null); }}>‹ Station</button>
                <span style={{ fontWeight: 600 }}>{selectedPos.Name}</span>
                <button style={styles.linkBtn} onClick={() => ApiBackend.firebase.auth().signOut()}>Logga ut</button>
            </div>

            {feedback &&
                <div style={{ ...styles.banner, background: bannerColor(feedback.type) }}>
                    <div style={styles.bannerTitle}>{feedback.title}</div>
                    {feedback.detail && <div style={styles.bannerDetail}>{feedback.detail}</div>}
                </div>
            }

            <div style={styles.body}>
                {!scanning &&
                    <button style={styles.primaryBtn} onClick={startScanner}>Starta skanning</button>
                }

                <div id={READER_ID} style={{ width: "100%", display: scanning ? "block" : "none", borderRadius: 12, overflow: "hidden" }} />

                {scanning &&
                    <button style={styles.secondaryBtn} onClick={() => { stopScanner(); setScanning(false); }}>Stoppa kameran</button>
                }

                {busy && <div style={{ textAlign: "center", padding: 12, color: "#555" }}>Arbetar…</div>}

                {order &&
                    <div style={styles.orderCard}>
                        <div style={styles.orderHead}>
                            <div style={{ fontWeight: 700 }}>{order.publicId}</div>
                            <div>{contactName(order)}</div>
                        </div>
                        {targetItems.length === 0 &&
                            <div style={{ color: "#888", padding: "6px 0" }}>Inga incheckningsbara produkter för denna station.</div>}
                        {targetItems.map((it, idx) => {
                            const delivered = numDeliveredFor(it, deliveries);
                            const full = delivered >= it.numberOfProducts;
                            return (
                                <div key={idx} style={styles.itemRow}>
                                    <div style={{ flex: 1 }}>
                                        <div>{labelForItem(it)}</div>
                                        <div style={{ fontSize: 13, color: full ? "#1f7a3d" : "#888" }}>
                                            Incheckade: {delivered} / {it.numberOfProducts}
                                        </div>
                                    </div>
                                    <button
                                        style={{ ...styles.deliverBtn, opacity: full || busy ? 0.4 : 1 }}
                                        disabled={full || busy}
                                        onClick={() => manualDeliver(it)}
                                    >
                                        {full ? "Klar" : "Checka in"}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                }
            </div>
        </div>
    );
};

const styles: { [k: string]: React.CSSProperties } = {
    page: { maxWidth: 520, margin: "0 auto", minHeight: "100vh", background: "#f4f6f8", display: "flex", flexDirection: "column" },
    center: { display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", color: "#666" },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px", background: "#2772b6", color: "#fff", fontSize: 17, position: "sticky", top: 0, zIndex: 5 },
    linkBtn: { background: "transparent", border: "none", color: "#fff", fontSize: 16, padding: 0, cursor: "pointer" },
    body: { padding: 14, display: "flex", flexDirection: "column", gap: 14 },
    stationList: { display: "flex", flexDirection: "column", gap: 10 },
    stationBtn: { padding: "18px 16px", fontSize: 18, borderRadius: 12, border: "1px solid #d0d7de", background: "#fff", textAlign: "left", cursor: "pointer" },
    primaryBtn: { padding: "18px", fontSize: 19, fontWeight: 600, borderRadius: 12, border: "none", background: "#2772b6", color: "#fff", cursor: "pointer" },
    secondaryBtn: { padding: "14px", fontSize: 16, borderRadius: 12, border: "1px solid #d0d7de", background: "#fff", cursor: "pointer" },
    banner: { color: "#fff", padding: "18px 16px", textAlign: "center" },
    bannerTitle: { fontSize: 22, fontWeight: 700 },
    bannerDetail: { fontSize: 15, marginTop: 4, opacity: 0.95 },
    orderCard: { background: "#fff", borderRadius: 12, border: "1px solid #e0e5ec", padding: 12 },
    orderHead: { display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 8, marginBottom: 8 },
    itemRow: { display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderTop: "1px solid #f2f2f2" },
    deliverBtn: { padding: "12px 16px", fontSize: 16, fontWeight: 600, borderRadius: 10, border: "none", background: "#1f7a3d", color: "#fff", cursor: "pointer", minWidth: 110 },
};

export default MobilePos;
